import {
    AirClass,
    AirFare,
    AirFareOption,
    CruiseExpiration,
    FareOptionsResponse,
    Gateway,
    Occupancy,
    RequestAirFare,
    SingleFareOptions,
    VsOption,
} from "@ssc-booking/api-contracts"
import { FareCode } from "@silversea-ssc/web-fares-fp"
import * as F from "fp-ts/function"
import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import { CruiseCode } from "@ssc-booking/functional-support"
import { AirClassOption, FeasibleAirFares } from "../feasible-air-fares"
import { FareCard, FareCardIndex, FareCardKey } from "../fare-card"
import { airParams } from "../../../../../../test-support/common"

export const makeAirFareOption = (airFare: AirFare, amount: number, isPromotional: boolean): AirFareOption =>
    AirFareOption.build({
        airFare,
        detailByGuest: [
            { paxNumber: 1, amount: { currency: "EUR", value: amount }, isPromotional },
            { paxNumber: 2, amount: { currency: "EUR", value: amount }, isPromotional },
        ],
    })

export const makeAirClassOption = (airFare: AirClass, amount: number, isPromotional: boolean): AirClassOption => ({
    airFare,
    detailByGuest: [
        { paxNumber: 1, amount: { currency: "EUR", value: amount }, isPromotional },
        { paxNumber: 2, amount: { currency: "EUR", value: amount }, isPromotional },
    ],
})

export const makeSingleFareOption = (
    fareCode: FareCode,
    airFares: AirFareOption[],
    gateways: readonly Gateway[] = [],
): SingleFareOptions =>
    SingleFareOptions.build({
        fareCode,
        airFares,
        vs: O.some(VsOption.build({ percentage: 5, type: "VS" })),
        gateways,
        d2d: O.none,
        promotions: [],
    })

export const flightConfigWith = (airFareOption: AirFareOption) => ({
    type: "flightsOrCruiseOnly" as const,
    airFareOption,
})

export const makeFareOptions = (cruiseCode: string, options: RONEA.ReadonlyNonEmptyArray<SingleFareOptions>) =>
    FareOptionsResponse.build({
        cruiseCode: CruiseCode.buildUnsafe(cruiseCode),
        cruiseExpiration: CruiseExpiration.build({ days: 7, date: new Date("2024-06-26T00:00:00Z") }),
        occupancy: Occupancy.of(2),
        options: options,
    })

export const makeFareCard = (
    index: number,
    key: string,
    fare: FareCode,
    airFare: RequestAirFare,
    options: SingleFareOptions,
    feasibleAirFares: FeasibleAirFares,
): FareCard => ({
    index: F.pipe(
        index,
        FareCardIndex.decode,
        E.getOrElse((): FareCardIndex => {
            throw new Error("Invalid FareCardIndex")
        }),
    ),
    key: F.pipe(
        key,
        FareCardKey.decode,
        E.getOrElse((): FareCardKey => {
            throw new Error("Invalid FareCardKey")
        }),
    ),
    fare,
    options,
    feasibleAirFares,
    status: "unknown",
    vsEligibility: "eligible",
    air: airParams(airFare),
    fareLabel: fare,
    suitePrices: [],
})
